import { useEffect, useState } from "react";
import { api } from "../lib/api";

// v146.33 — SeoHead. Fetches `/api/seo/meta/*`, then writes <title>,
// <meta description>, canonical, OpenGraph, Twitter card, and a JSON-LD
// <script> block directly into <head>. Direct DOM writes (instead of
// react-helmet-async) sidestep the version-3 dedupe issues we hit and
// give us total control over which pre-existing static tags to override.
//
// Rationale for direct DOM writes:
//   - Google's second-pass renderer executes JS and re-reads <head>
//     after the SPA hydrates, so what we write here IS what indexes.
//   - Twitterbot / facebookexternalhit don't run JS but fall back to
//     the static defaults we ship in public/index.html — good enough.
//   - JSON-LD is picked up by Google + AI crawlers (Perplexity /
//     GPT-User / ClaudeBot) that do run JS on demand.

const MANAGED_ATTR = "data-seohead";
const STATIC_KEEP = new Set(["og:site_name", "og:type", "twitter:card"]);

// Selector list of static duplicables we replace on public routes.
const DUP_SELECTORS = [
  'meta[name="description"]',
  'meta[property^="og:"]',
  'meta[name^="twitter:"]',
  'link[rel="canonical"]',
];

function _stashStatic() {
  // On first SeoHead mount, snapshot + remove the duplicable static tags.
  // On last SeoHead unmount, restore them so navigating back to /
  // marketing routes gets their default SEO back.
  if (typeof document === "undefined") return [];
  const stash = [];
  DUP_SELECTORS.forEach((sel) => {
    document.querySelectorAll(sel).forEach((el) => {
      if (el.hasAttribute(MANAGED_ATTR)) return;
      const key = el.getAttribute("property") || el.getAttribute("name") || el.tagName;
      if (STATIC_KEEP.has(key)) return;
      stash.push(el.outerHTML);
      el.remove();
    });
  });
  return stash;
}

function _upsert(tag, attrs, content) {
  // Idempotent upsert for meta / link / script elements managed by
  // SeoHead. Identified by [data-seohead-key] so we never leave stale
  // tags behind between re-renders or route changes.
  if (typeof document === "undefined") return;
  const key = attrs.property || attrs.name || attrs.rel || tag;
  const sel = `${tag}[${MANAGED_ATTR}="${key}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement(tag);
    el.setAttribute(MANAGED_ATTR, key);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => {
    if (v == null) return;
    el.setAttribute(k, v);
  });
  if (content != null) {
    el.textContent = content;
  }
}

function _removeManaged() {
  if (typeof document === "undefined") return;
  document.head.querySelectorAll(`[${MANAGED_ATTR}]`).forEach((el) => el.remove());
}

export default function SeoHead({ endpoint, fallback }) {
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    const stash = _stashStatic();
    return () => {
      _removeManaged();
      // Restore static defaults on unmount.
      stash.forEach((html) => {
        const wrap = document.createElement("div");
        wrap.innerHTML = html.trim();
        const el = wrap.firstElementChild;
        if (el) document.head.appendChild(el);
      });
    };
  }, []);

  useEffect(() => {
    if (!endpoint) return;
    let alive = true;
    // v146.36 — Clear stale meta the moment the endpoint changes so
    // the title effect below immediately falls back to the generic
    // "Linkola" title. Without this, navigating between two /u/{slug}
    // pages leaves the OLD creator's title paired with the NEW URL for
    // ~200ms while the new fetch is in flight — Safari's autocomplete
    // and history snapshot that pair and cache it forever (leading to
    // e.g. "Jawed Habib — Saloon & Beauty · Linkola" showing up as the
    // suggestion for linkola.app/u/linkola).
    setMeta(null);
    api.get(endpoint)
      .then(({ data }) => { if (alive) setMeta(data); })
      .catch(() => { if (alive) setMeta(null); });
    return () => { alive = false; };
  }, [endpoint]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const title = meta?.title || fallback?.title || "Linkola";
    const description = meta?.description || fallback?.description
      || "Linkola is the AI growth co-pilot for Instagram creators and D2C brands.";
    const canonical = meta?.canonical || fallback?.url || window.location.href;
    const image = meta?.image || fallback?.image || "https://linkola.app/icon-512.png";
    const og = meta?.og || {
      title, description, url: canonical, image, type: "website", site_name: "Linkola",
    };
    const tw = meta?.twitter || {
      card: image ? "summary_large_image" : "summary", title, description, image,
    };

    document.title = title;
    _upsert("meta", { name: "description", content: description });
    _upsert("link", { rel: "canonical", href: canonical });

    _upsert("meta", { property: "og:title",       content: og.title || title });
    _upsert("meta", { property: "og:description", content: og.description || description });
    _upsert("meta", { property: "og:url",         content: og.url || canonical });
    _upsert("meta", { property: "og:type",        content: og.type || "website" });
    _upsert("meta", { property: "og:site_name",   content: og.site_name || "Linkola" });
    if (og.image) _upsert("meta", { property: "og:image", content: og.image });

    _upsert("meta", { name: "twitter:card",        content: tw.card || "summary" });
    _upsert("meta", { name: "twitter:title",       content: tw.title || title });
    _upsert("meta", { name: "twitter:description", content: tw.description || description });
    if (tw.image) _upsert("meta", { name: "twitter:image", content: tw.image });

    // JSON-LD structured data
    if (meta?.jsonld) {
      _upsert("script", {
        type: "application/ld+json",
      }, JSON.stringify(meta.jsonld));
    }
  }, [meta, fallback]);

  return null;
}
